import { Fragment, useState } from "react";

import i18next from "i18next";
import moment from "moment";
import { BiChevronRightCircle } from "react-icons/bi";
import { useSelector } from "react-redux";

import classNames from "classnames";
import { PiHandbagSimple, PiSuitcaseRolling } from "react-icons/pi";
import { webTransferTimeFormat } from "../../../config";
import {
  checkIsNightFlightAndIsChangeAirport,
  getCityString,
  getDayByFormat,
  getFreeSeatsLabel,
  getServiceClass,
  getTransferTime,
} from "../../../utils";
import {
  AirlineAndSitsBlock,
  AirlineLogo,
  ArrivalBlock,
  CharterBadge,
  DepartureBlock,
  DetailsAndRulesBlock,
  DetailsButton,
  DurationBlock,
  FlightDetails,
  FlightNumber,
  FlightNumbersBlock,
  FlightSegment,
  FlightSegmentIcons,
  FlightSegmentTimeAircraft,
  FlightSegmentTimings,
  FlightTimingsBlock,
  FlightTitle,
  InfoWrapper,
  SitsCount,
  TransferBlock,
  TransferTime,
  Wrapper,
} from "./style";

const RecommendationFlightItemComponent = ({ route, validatingSupplier }) => {
  const { included } = useSelector(state => state.resultData);
  const { language, t } = i18next;

  const [isShowDetails, setIsShowDetails] = useState(false);

  const departureSegment = route.segments[0];
  const arrivalSegment = route.segments[route.segments.length - 1];

  const renderDepartureInfo = () => {
    const { time, airport } = departureSegment.departure;
    calculatePercents();

    return (
      <DepartureBlock>
        <span className="airport">{airport}</span>
        <span className="time">{time.split(" ")[1]}</span>
        <span className="date">{getDayByFormat(time, "ddd DD MMM", language)}</span>
      </DepartureBlock>
    );
  };

  const renderArrivalInfo = () => {
    const { time, airport } = arrivalSegment.arrival;

    return (
      <ArrivalBlock>
        <span className="airport">{airport}</span>
        <span className="time">{time.split(" ")[1]}</span>
        <span className="date">{getDayByFormat(time, "ddd DD MMM", language)}</span>
      </ArrivalBlock>
    );
  };

  const renderDurationTime = duration => {
    const hours = Math.trunc(duration / 3600);
    const minutes = Math.floor((duration - hours * 3600) / 60);

    return `${hours} ${t("hour_short")} ${minutes} ${t("minutes_short")}`;
  };

  const calculatePercents = () => {
    const parts = [];

    route.segments.forEach((segment, index) => {
      if (index === 0) {
        parts.push({
          percent: ((segment.duration * 100) / route.duration).toFixed(2),
          type: "segment",
          info: `${segment.operation_supplier}-${segment.carrier_number}, ${renderDurationTime(segment.duration)}`,
        });
      } else {
        const prevDate = route?.segments[index - 1]?.arrival.time;
        const actualDate = segment.departure.time;
        const prevDateUnix = moment(prevDate, webTransferTimeFormat).unix();
        const actualDateUnix = moment(actualDate, webTransferTimeFormat).unix();
        const unixDifference = actualDateUnix - prevDateUnix;

        parts.push({
          percent: ((unixDifference * 100) / route.duration).toFixed(2),
          type: "transfer",
          info: `${t("transfer")}: ${getCityString(
            included,
            language,
            segment.departure.city,
            segment.departure.airport
          )}, ${segment.departure.airport}, (${getTransferTime(
            route,
            segment.index,
            t("hour_short"),
            t("minutes_short")
          )})`,
        });
        parts.push({
          percent: ((segment.duration * 100) / route.duration).toFixed(2),
          type: "segment",
          info: `${segment.operation_supplier}-${segment.carrier_number}, ${renderDurationTime(segment.duration)}`,
        });
      }
    });

    return (
      <div className="flight-line">
        {parts.map((part, index) => (
          <div
            style={{
              height: part.type === "transfer" ? 2 : 4,
              width: `${part.percent}%`,
            }}
            key={index}
            title={part.info}
          >
            <span></span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Wrapper>
      <FlightTitle isOrange={route.index === 1 || route.index === 3}>
        {getCityString(included, language, departureSegment.departure.city, departureSegment.departure.airport)} ➞{" "}
        {getCityString(included, language, arrivalSegment.arrival.city, arrivalSegment.arrival.airport)}
      </FlightTitle>

      <InfoWrapper>
        <AirlineAndSitsBlock>
          <AirlineLogo src={`/suppliers/${validatingSupplier}.png`} alt={`supplier-${validatingSupplier}-logo`} />
          <SitsCount>
            {getFreeSeatsLabel(departureSegment.free_seats, t("free_seats_1"), t("free_seats_2_4"), t("free_seats_5"))}
          </SitsCount>
          {departureSegment.is_charter && <CharterBadge>{t("charter")}</CharterBadge>}
        </AirlineAndSitsBlock>

        <FlightNumbersBlock>
          {route.segments.map((segment, index) => (
            <FlightNumber key={index}>
              {segment.operation_supplier}-{segment.carrier_number}
            </FlightNumber>
          ))}
        </FlightNumbersBlock>

        <FlightTimingsBlock>
          {renderDepartureInfo()}

          <DurationBlock>
            <div className="duration-time">{renderDurationTime(route.duration)}</div>
            {calculatePercents()}
            <div className="segments">
              {route.segments
                .slice(0, -1)
                .map(segment => segment.arrival.airport)
                .join(", ")}
            </div>
          </DurationBlock>

          {renderArrivalInfo()}
        </FlightTimingsBlock>
      </InfoWrapper>

      <DetailsAndRulesBlock>
        <DetailsButton onClick={() => setIsShowDetails(prev => !prev)}>
          {t("flight_details")}{" "}
          <span className={classNames({ active: isShowDetails })}>
            <BiChevronRightCircle />
          </span>
        </DetailsButton>
      </DetailsAndRulesBlock>

      {isShowDetails && (
        <FlightDetails>
          {route.segments.map((segment, index) => {
            let aircraftName = segment.aircraft;

            if (included.aircraft[segment.aircraft]) {
              aircraftName = included.aircraft[segment.aircraft].name.en;
            } else {
              aircraftName = "";
            }

            const isTransfer = index > 0;
            let transferTime = "";

            if (isTransfer) {
              transferTime = getTransferTime(route, index, t("hour_short"), t("minutes_short"));
            }

            const [isChangeAirport, isNightTransfer] = checkIsNightFlightAndIsChangeAirport(route, index);

            return (
              <Fragment key={index}>
                {isTransfer && (
                  <TransferBlock>
                    <TransferTime>
                      {getCityString(included, language, segment.departure.city, segment.departure.airport)}:{" "}
                      {t("transfer")} {transferTime}
                      {isChangeAirport && `, ${t("change_airport")}`}
                      {isNightTransfer && `, ${t("night_flight")}`}
                    </TransferTime>
                  </TransferBlock>
                )}

                <FlightSegment>
                  <div>
                    <AirlineLogo
                      src={`/suppliers/${segment.operation_supplier}.png`}
                      alt={`supplier-${segment.operation_supplier}-logo`}
                    />
                  </div>

                  <div className="flightInfo">
                    <FlightSegmentTimeAircraft>
                      <div className="flightAndDuration">
                        <div>
                          {t("flight")} {segment.operation_supplier}-{segment.carrier_number}
                        </div>
                        <div className="duration">
                          {t("on_way")} {renderDurationTime(segment.duration)}
                        </div>
                      </div>
                      {aircraftName.length ? <div>{aircraftName}</div> : null}

                      <FlightSegmentTimings>
                        <div className="flightTime">
                          {segment.departure.time.split(" ")[1]}{" "}
                          <span>{getDayByFormat(segment.departure.time, "ddd DD MMM", language)}</span>
                        </div>
                        <div className="flightPlace">
                          <div className="flightPlaceCode">{segment.departure.airport}</div>
                          <div className="flightPlaceName">
                            <span>
                              {getCityString(included, language, segment.departure.city, segment.departure.airport)}
                            </span>
                          </div>
                        </div>
                      </FlightSegmentTimings>

                      <FlightSegmentTimings>
                        <div className="flightTime">
                          {segment.arrival.time.split(" ")[1]}{" "}
                          <span>{getDayByFormat(segment.arrival.time, "ddd DD MMM", language)}</span>
                        </div>
                        <div className="flightPlace">
                          <div className="flightPlaceCode">{segment.arrival.airport}</div>
                          <div className="flightPlaceName">
                            <span>
                              {getCityString(included, language, segment.arrival.city, segment.arrival.airport)}
                            </span>
                          </div>
                        </div>
                      </FlightSegmentTimings>
                    </FlightSegmentTimeAircraft>
                  </div>

                  <div className="baggageCabinInfo">
                    {getServiceClass(segment.service_class)}
                    <FlightSegmentIcons>
                      <div
                        className={classNames({ hasNotBaggage: !segment.hand_luggage })}
                        title={segment.hand_luggage ? t("yes_hand_luggage") : t("no_hand_luggage")}
                      >
                        <PiHandbagSimple />
                      </div>

                      <div
                        className={classNames({ hasNotBaggage: !segment.baggage })}
                        title={segment.baggage ? t("yes_baggage") : t("no_baggage")}
                      >
                        <PiSuitcaseRolling />
                      </div>
                    </FlightSegmentIcons>
                  </div>
                </FlightSegment>
              </Fragment>
            );
          })}
        </FlightDetails>
      )}
    </Wrapper>
  );
};

export default RecommendationFlightItemComponent;
